import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '@/lib/api';
import { Eye, EyeOff, Target } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cdfce993-612e-4617-ac7b-89da32b19bd7/artifacts/wcwsdulx_Logo%20benchory.png";

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authApi.login({ email, password });
      localStorage.setItem('benchory_token', res.data.access_token);
      localStorage.setItem('benchory_user', JSON.stringify(res.data.user));
      onLogin(res.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 bg-[#1B2A4A] flex-col items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full border border-white/20" />
          <div className="absolute bottom-32 right-16 w-48 h-48 rounded-full border border-white/20" />
          <div className="absolute top-1/2 left-1/3 w-32 h-32 rounded-full border border-white/10" />
        </div>
        <div className="relative z-10 text-center max-w-md">
          <img src={LOGO_URL} alt="BENCHORY" className="w-56 mx-auto mb-10 drop-shadow-lg" />
          <h2 className="text-white text-2xl font-bold tracking-tight mb-4 font-manrope">
            La transparence crée le progrès
          </h2>
          <p className="text-white/60 text-base leading-relaxed">
            Pilotez votre entreprise avec des benchmarks comparables, 
            des priorités chiffrées et un coût de l'inaction mensuel.
          </p>
          <div className="mt-12 flex gap-6 justify-center">
            <div className="text-center">
              <div className="text-[#B8860B] text-3xl font-bold">15+</div>
              <div className="text-white/50 text-xs mt-1">Indicateurs clés</div>
            </div>
            <div className="w-px bg-white/20" />
            <div className="text-center">
              <div className="text-[#B8860B] text-3xl font-bold">60s</div>
              <div className="text-white/50 text-xs mt-1">Diagnostic express</div>
            </div>
            <div className="w-px bg-white/20" />
            <div className="text-center">
              <div className="text-[#B8860B] text-3xl font-bold">30j</div>
              <div className="text-white/50 text-xs mt-1">Plan d'action</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8F9FA]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex justify-center mb-8">
            <img src={LOGO_URL} alt="BENCHORY" className="w-40" />
          </div>
          <div className="bg-white rounded-xl shadow-[0_2px_8px_rgba(27,42,74,0.06)] p-8 border border-gray-100">
            <h1 className="text-2xl font-bold text-[#1B2A4A] mb-1 font-manrope" data-testid="login-title">
              Connexion
            </h1>
            <p className="text-gray-400 text-sm mb-8">Accédez à votre espace de pilotage</p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Email</label>
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-11 rounded-lg border border-gray-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent transition-all"
                  placeholder="votre@email.fr"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Mot de passe</label>
                <div className="relative">
                  <input
                    data-testid="login-password-input"
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-11 rounded-lg border border-gray-200 bg-white px-4 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A] focus:border-transparent transition-all"
                    placeholder="Votre mot de passe"
                    required
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#1B2A4A] text-white rounded-lg font-semibold text-sm hover:bg-[#152039] transition-all duration-200 shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Target size={16} />
                    Se connecter
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <Link to="/mot-de-passe-oublie" className="text-sm text-gray-400 hover:text-[#1B2A4A] block" data-testid="login-forgot-password">
                Mot de passe oublié ?
              </Link>
              <Link to="/inscription" className="text-sm text-[#B8860B] hover:underline font-medium" data-testid="login-register-link">
                Pas encore de compte ? S'inscrire
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
